import { SHARING } from "../share/shareView";
import { PAGE_SLOT_COUNT } from "../util/slotCount";
import storage from "../util/storage";

export const PHONE_REGEX = /\b\d{10}\b/g;
export const EMAIL_REGEX = /^[a-zA-Z0-9+_.-]+@[a-zA-Z0-9.-]+$/;
// export const PASSWORD_REGEX =
//   /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,16}$/;
export const PASSWORD_REGEX =
  /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,16}$/;
export const OTP_REGEX = /^[0-9]*$/;

export const NUMBER_REGEX = /^[0-9]*$/;
// export const SITE_URL_REGEX = /^(http(s):\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/;
export const SITE_URL_REGEX =
  /^((https|http?|ftp|smtp):\/\/)?(www.)?[a-z0-9?!.*-\s]+\.[a-z]+(\/[a-zA-Z0-9#]+\/?)*$/;
export const SEARCH_REGEX = /^[a-zA-Z0-9\s]*$/;

// This app's real plan-tier data windows (subscriptionplans DB collection,
// source of truth per Chris's own 2026-07-24 call): Base Jump shows 30 days
// in the dashboard; behind that, Skydive quietly holds a 90-day total grace
// period (raw data isn't actually purged until 90 days, only hidden from
// view past 30) specifically so an upgrade can restore it; paid tiers get a
// full 365-day window. Hardcoded rather than fetched - static plan facts,
// not account-specific data.
export const BASE_JUMP_VISIBLE_DAYS = 30;
export const FREE_TIER_RAW_RETENTION_DAYS = 90;
export const PAID_TIER_RETENTION_DAYS = 365;

// Second rewrite 2026-07-24 - the first version framed this as LOSS ("142
// sessions will be erased"), which is actually confusing/alarming for a
// Base Jump user: they can only ever SEE 30 days, so they have no idea an
// extra 60 days of raw data is even being held for them - a message about
// "erasing" data they never saw reads as "erasing something I never had."
// Chris's own correction: this needs OPPORTUNITY framing instead - there's
// bonus historical data sitting in a grace period specifically for people
// who might upgrade later, and that window is closing. "Unlock," not
// "keep" - keep would wrongly imply they already had access to it.
// The banner itself has no room to explain the 30-vs-90 distinction, so it
// stays short and links to Settings - which now surfaces this as a normal
// row in the existing Subscription table (label/value/action, same as the
// Page Views Used and Data Storage Limit rows next to it) rather than a
// separate paragraph/banner, since a real explanation reads better as
// account information than as an alert.
// Leads with "Don't miss" (not "don't lose") - Chris's own instinct was to
// add a punchier hook up front, but "lose" reintroduces the exact loss-
// framing confusion already fixed once (Base Jump users only ever SEE 30
// days, so "lose" implies losing something they never had access to).
// "Miss" keeps the urgency/FOMO energy while staying in the opportunity
// register - missing a chance to unlock something, not losing a possession.
// "Reserved" (not "Free" - redundant on an already-free plan; not "Hidden" -
// risks reading as concealment rather than a favor) frames the 90-day grace
// period as something Skydive set aside FOR the user.
export const formatDataDeletionWarningBannerMessage = (
  dataDeletionDate: Date | string | null
): string => {
  const dateClause = dataDeletionDate
    ? new Date(dataDeletionDate).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "soon";

  return `Don't miss your reserved bonus history! It expires soon - ${dateClause}.`;
};

// Settings.tsx's Subscription table uses these three raw constants directly
// (Bonus History row, next to Data Storage Limit) rather than a pre-built
// sentence, since a table row needs the pieces separately (value column vs.
// muted note column), not one paragraph.
export const EMPTY_SPACE_REGEX = /^\s+$/;

export const limit = 10;

export const admnRole = "superuser";
export const typeText = "text";
export const typePassword = "password";
export const fourteenDaysPlan = "14 Days Trial";
export const baseJumpPlan = "Base Jump";
export const resetAdminPasswordPath = "/resetAdminPassword";
export const resetPasswordPath = "/resetPassword";

export type SortingDirection = "asc" | "desc";
export type ColumnNames = "name" | "currentVisitors" | "overallVisitors";
export type LimitType = "Daily" | "Monthly" | "EveryMinute";

export const adminAction = {
  EDIT: "edit",
  DELETE: "delete",
};

export const breadCrumb = {
  ADMIN: "Admin",
  DELETED_USERS: "Deleted Users",
  USERS: "Users",
  AUDIT_LOG: "Audit Log",
  CANCELLATION_FEEDBACK: "Cancellation Feedback",
  ONBOARDING_INSIGHTS: "Onboarding Insights",
  GROWTH: "Growth",
};

export const signUpQuizRole = [
  { name: "Network administrator", value: "Network administrator" },
  { name: "Information Technology", value: "Information Technology" },
  { name: "Designer / UX", value: "Designer / UX" },
  { name: "Data Analyst", value: "Data Analyst" },
  { name: "Programmer", value: "Programmer" },
  { name: "Marketing", value: "Marketing" },
  { name: "Sales / Business", value: "Sales / Business" },
  { name: "Support tech", value: "Support tech" },
  { name: "Executive", value: "Executive" },
  { name: "Manager", value: "Manager" },
  { name: "Website owner", value: "Website owner" },
  { name: "Other", value: "Other" },
];
export const siteType = [
  { name: "E-commerce", value: "E-commerce" },
  {
    name: "Content website (Blog, news, media)",
    value: "Content website (Blog, news, media)",
  },
  { name: "Social media", value: "Social media" },
  { name: "Business/Company website", value: "Business/Company website" },
  { name: "Landing page", value: "Landing page" },
  {
    name: "Review/directory/wiki website",
    value: "Review/directory/wiki website",
  },
  { name: "Portfolio", value: "Portfolio" },
  { name: "Marketing website", value: "Marketing website" },
  { name: "Forum / Knowledge base", value: "Forum / Knowledge base" },
  { name: "Forum / Knowledge base", value: "Forum / Knowledge base" },
  { name: "Other", value: "Other" },
];
export const siteMonthlyUsers = [
  { name: "1 - 1,000", value: "1 - 1,000" },
  { name: "1,000 - 10k", value: "1,000 - 10k" },
  { name: "10k - 100k", value: "10k - 100k" },
  { name: "100k - 500k", value: "100k - 500k" },
  { name: "500k - 1M", value: "500k - 1M" },
  { name: "1M - 5M", value: "1M - 5M" },
  { name: "5M and higher", value: "5M and higher" },
];
export const optionForDataSelection = [
  { name: "Country", value: "country_name" },
  { name: "Operating System", value: "os" },
  { name: "Browser", value: "browser" },
  { name: "Traffic Source", value: "traffic_source" },
  { name: "Search Engine", value: "search_engine" },
  { name: "Social Network", value: "social_network" },
  { name: "Referral Link", value: "referrer" },
  { name: "Visitor Type", value: "visitor_type" },
  { name: "Device Type", value: "device" },
];

export const initialHistoryDataDateRange = [
  { name: "Today", value: new Date().setHours(0, 0, 0, 0) },
  {
    name: "Last 2 Days",
    value: new Date(
      new Date().setHours(0, 0, 0, 0) - 1 * 24 * 60 * 60 * 1000
    ).getTime(),
  },
  {
    name: "Last 7 Days",
    value: new Date(
      new Date().setHours(0, 0, 0, 0) - 6 * 24 * 60 * 60 * 1000
    ).getTime(),
  },
  {
    name: "Last 30 Days",
    value: new Date(
      new Date().setHours(0, 0, 0, 0) - 29 * 24 * 60 * 60 * 1000
    ).getTime(),
  },
  { name: "All", value: storage.get("userCreatedAt") },
  { name: "Custom Date" },
];
export const reasonForClosingAccount = [
  {
    name: "I'm not getting any value from skydive",
    value: "I'm not getting any value from skydive",
  },
  {
    name: "I have privacy or security concerns",
    value: "I have privacy or security concerns",
  },
  {
    name: "I use another analytics program",
    value: "I use another analytics program",
  },
  {
    name: "I just don't want to use this any longer",
    value: "I just don't want to use this any longer",
  },
  {
    name: "It doesn't work as I would like it to",
    value: "It doesn't work as I would like it to",
  },
  { name: "I'm getting too much email", value: "I'm getting too much email" },
  { name: "Other", value: "Other" },
];

export const DEFAULT_D3CIRCLE_CONSTRAINT = {
  gravity: 0.2,
  charge: -1,
  friction: 0.001,
  speed: 100,
};
export const arrowUp = "ArrowUp";
export const arrowDown = "ArrowDown";
export const codeVerified = "Your Mail is verified successfully";
export const adminCodeVerified = "Your code is verified successfully";

export const speedOptions = [
  { value: 100, textField: "Fast" },
  { value: 1000, textField: "Medium" },
  { value: 5000, textField: "Slow" },
];
export const charge = {
  min: -100,
  max: -1,
  step: 1,
};
export const friction = {
  min: 0.0001,
  max: 0.3,
  step: 0.001,
};

export const DEFAULT_VALUE = {
  // 12 real page slots now (was 10) - see clusterService.ts's own
  // UNKNOWN_URL_CLUSER_INDEX comment for the full index remap. This is a
  // SEPARATE copy of that constant (clusterService.ts has its own local
  // DEFAULT_VALUE object too, not imported from here) - both need to stay
  // in sync by hand.
  UNKNOWN_URL_CLUSER_INDEX: PAGE_SLOT_COUNT,
  ALL_CLUSTER_INDEX: PAGE_SLOT_COUNT + 2,
  DEFAULT_CLUSTER_CENTER: {
    "menu-nodes": {
      centerIndex: 13,
      cluster: 13,
    },
  },
  SETUP_TIMEOUT: 15000,
  ALL_RANGE_NAME: "All",
  ROW_PER_PAGE: 100,
};
export const INITIAL_FILTERS = [
  // Plain, unclustered bubbles - no categorization, no icons, no
  // per-category sidebar rows. Special-cased throughout clusterService.ts
  // (search for menuType === "none") since every other filter requires
  // sessionData[menuType] to be truthy just for a bubble to draw at all -
  // "None" bypasses that gate entirely rather than trying to satisfy it.
  {
    name: "No Filter",
    menuType: "none",
  },
  {
    name: "Country",
    menuType: "country_name",
  },
  {
    name: "Operating System",
    menuType: "os",
  },
  {
    name: "Browser",
    menuType: "browser",
  },
  {
    name: "Traffic Source",
    menuType: "traffic_source",
  },
  {
    name: "Search Engine",
    menuType: "search_engine",
  },
  // Which social network a visit came from (backend Common/Helper/trafficSource.ts SOCIAL_NETWORKS). Empty for every visit that is not social, so "Hide unknown" (Display
  // settings) is what makes this view show only the social visits and their shares.
  {
    name: "Social Network",
    menuType: "social_network",
  },
  {
    name: "Referral Link",
    menuType: "referrer",
  },
  {
    name: "New vs Returning",
    menuType: "visitor_type",
  },
  {
    name: "Device Type",
    menuType: "device",
  },
  // Bot/human/AI classification - "Visitor Type" was freed up from the
  // new-vs-returning filter above (renamed "New vs Returning") specifically
  // to give this the more natural name, per direct decision 2026-07-09.
  // Backed by a NEW session field, visitor_category (see Session.ts) -
  // distinct from the existing visitor_type field above, which the new
  // field's name deliberately avoids colliding with.
  {
    name: "Visitor Type",
    menuType: "visitor_category",
  },
];

// Shared with Settings.tsx's page-views-used row (both surfaces should agree
// on when a customer is "close to their limit") - was only defined locally
// in RightMenu.tsx.
export const PAGE_VIEWS_WARNING_THRESHOLD_PERCENT = 80;

export const UseFulLinks = {
  installingSkydive: "/help/installing-skydive",
  contactUs: "https://go.crisp.chat/chat/embed/?website_id=a14a2c2a-be82-4404-a9cd-6c115663c0b0",
};

export const adminStatus = [
  { name: "Active", value: "active" },
  { name: "Inactive", value: "inactive" },
];

export const adminRole = [
  { name: "Restricted", value: "restricted" },
  { name: "Superuser", value: "superuser" },
];

export const userExportCsvOption = {
  CSV_HEADERS: [
    "NAME",
    "EMAIL",
    "SITE URL",
    "DATE REGISTERED",
    "TIME TO CONVERT OR PAY",
    // "SUBSCRIPTION PLAN",
    // "ACTIONS",
  ],
  FILE_NAME: "skyDiveRegisterUsers.csv",
};

export const adminExportCsvOption = {
  CSV_HEADERS: ["NAME", "EMAIL", "ROLE", "STATUS", "PERMISSIONS"],
  FILE_NAME: "skyDiveRegisterAdmins.csv",
};

// export const adminListHeaders = [
//   "NAME",
//   "EMAIL",
//   "USER ROLE",
//   "STATUS",
//   "PERMISSIONS",
// ];
// export const userListHeaders = [
//   "NAME",
//   "EMAIL",
//   "URL",
//   "DATE REGISTERED",
//   // "ACCOUNT TYPE",
//   // "STATUS",
//   "TIME TO CONVERT OR PAY",
//   "SUBSCRIPTION PLAN",
//   "ACTIONS",
// ];

export const adminListActions = [
  { name: "Edit", value: "edit" },
  { name: "Delete", value: "delete" },
];

export const customAdminDetail = {
  EMAIL: "admin@gmail.com",
};

export const DATEPICKER_RANGES=["Today", "Last 2 Days", "Last 7 Days", "Last 30 Days", "All"];

// A goal is a bigger commitment than a tracked page (it's "the one thing
// that matters," not just one of up to 10 boxes), so kept deliberately
// lower than the page-slot caps (3/10, see pageSelectionOrRemoval in
// RightMenu.tsx) rather than matching them 1:1.
export const GOAL_LIMITS = { free: 1, paid: 5 };
// pageviews figures were out of sync with the real enforced values in the
// subscriptionplans DB collection (1000/50K/200K here vs the actual
// 10000/100K/300K limits LockService enforces) - a customer reading this
// "Compare Plans" tooltip was being quoted numbers smaller than what they'd
// actually get. Corrected to match the real DB values exactly (2026-07-24).
// dataStorage/urls/mainPageviews/websites were already correct.
// 🔴 THIS COMMENT WAS WRONG, AND SO WAS THE COPY IT DESCRIBED (fixed
// 2026-09-25). It claimed playbackAllowed/flowLinesAllowed/
// interactionPulsesAllowed are "all gated identically, on tier alone".
// Re-read against entitlements.ts, they are not:
//
//   playbackAllowed:          true            - EVERY tier
//   playbackDays:             isFreeTier ? 1 : -1
//   interactionPulsesAllowed: true            - EVERY tier, unconditional
//   flowLinesAllowed:         !isFreeTier     - the ONLY real gate
//
// So the upgrade surfaces were telling a Base Jump customer that upgrading
// grants Playback and Interaction pulses, which they already have. That
// under-sells the free tier and over-promises the paid one in the same
// sentence. Only flow lines, and the LENGTH of playback history, actually
// change.
export const paymentFormPlans = [
  {
    title: "Base Jump",
    cost: "$0",
    // "Per Month", not "Per 30 Days" (2026-08-30). Billing moved to a true
    // CALENDAR month on a fixed date in the 2026-08-15 migration - the Stripe
    // prices all recur "every 1 month", i.e. 12 charges a year, not 12.17.
    // "Per 30 Days" described the old rolling cycle and is now simply wrong on
    // a card the customer reads while deciding to pay.
    billingPeriod: "Per Month",
    description: "Our Forever Free Plan                             ",
    pageviews: "10K",
    websites: 1,
    dataStorage: 30,
    urls: "10",
    mainPageviews: 3,
    premiumFeaturesIncluded: false,
  },
  {
    title: "High Altitude",
    cost: "$39",
    // "Per Month", not "Per 30 Days" (2026-08-30). Billing moved to a true
    // CALENDAR month on a fixed date in the 2026-08-15 migration - the Stripe
    // prices all recur "every 1 month", i.e. 12 charges a year, not 12.17.
    // "Per 30 Days" described the old rolling cycle and is now simply wrong on
    // a card the customer reads while deciding to pay.
    billingPeriod: "Per Month",
    // Annual pricing (2026-08-13): ~17% off ("2 months free"), matches the
    // real Stripe annual Price object wired up in subscriptions.const.ts
    // (backend) - annualCost is a DISPLAY value only, same caveat as `cost`
    // above (the real charge is whatever the backend's annualStripeId
    // points to in Stripe).
    annualCost: "$390",
    annualBillingPeriod: "Per Year",
    description: "Low Traffic & Small Business",
    pageviews: "100K",
    websites: 1,
    dataStorage: 1,
    urls: "Unlimited",
    // The page slots every paid plan has (PAGE_SLOT_COUNT, 14 since 2026-10-25). The catalog row and each
    // account follow it through scripts/migrate-catalog-mainViewSlots.js and the nightly resync sweep.
    mainPageviews: PAGE_SLOT_COUNT,
    premiumFeaturesIncluded: true,
  },
  {
    title: "Stratosphere",
    cost: "$89",
    // "Per Month", not "Per 30 Days" (2026-08-30). Billing moved to a true
    // CALENDAR month on a fixed date in the 2026-08-15 migration - the Stripe
    // prices all recur "every 1 month", i.e. 12 charges a year, not 12.17.
    // "Per 30 Days" described the old rolling cycle and is now simply wrong on
    // a card the customer reads while deciding to pay.
    billingPeriod: "Per Month",
    // Same annual-pricing pass as High Altitude above - see its own comment.
    annualCost: "$890",
    annualBillingPeriod: "Per Year",
    description: "High Traffic, Multi-Site",
    pageviews: "300K",
    // Multi-site: Stratosphere now tracks 3 websites (2026-10-02, step 3b).
    websites: 3,
    dataStorage: 1,
    urls: "Unlimited",
    mainPageviews: PAGE_SLOT_COUNT,
    premiumFeaturesIncluded: true,
  },
  {
    title: "Exosphere",
    cost: "$199",
    billingPeriod: "Per Month",
    annualCost: "$1,990",
    annualBillingPeriod: "Per Year",
    description: "Scale & Enterprise",
    pageviews: "1M",
    websites: 5,
    dataStorage: 1,
    urls: "Unlimited",
    mainPageviews: PAGE_SLOT_COUNT,
    premiumFeaturesIncluded: true,
  },
];

// Shared label list for the premium features gated behind
// premiumFeaturesIncluded - one place so PaymentModal.tsx and
// TrialFinishModel.tsx's two separate copies of this list can't drift apart.
// What upgrading GENUINELY adds. Interaction pulses are gone from this list
// because they are unconditional, and playback is reworded because the free
// tier has it too - what changes is how far back it reaches.
// The replay-link numbers below are limitsFor() in skydive_backend/src/SessionApi/Helper/playbackShare.ts (free 1 link / 7 days, paid 10 / 30; the trial 3 / 14 is stated where the trial is described). Change both together.
export const PREMIUM_FEATURE_LABELS = [
  "Playback - full history replay",
  "Live flow lines",
  // only where sharing is switched on in this build (REACT_APP_PLAYBACK_SHARE): a plan card must not advertise what is off
  ...(SHARING ? ["Replay links - up to 10 at a time, 30 days each"] : []),
];

// What Base Jump already includes beyond its limits. Rendered on the free
// plan's card so it stops looking like a list of numbers with no features.
export const FREE_TIER_FEATURE_LABELS = [
  "Playback - replay yesterday",
  "Interaction pulses",
  ...(SHARING ? ["Replay link - 1 at a time, 7 days"] : []),
];

export const countries = [
  { name: "Afghanistan", value: "AF" },
  { name: "Ã…land Islands", value: "AX" },
  { name: "Albania", value: "AL" },
  { name: "Algeria", value: "DZ" },
  { name: "American Samoa", value: "AS" },
  { name: "Andorra", value: "AD" },
  { name: "Angola", value: "AO" },
  { name: "Anguilla", value: "AI" },
  { name: "Antarctica", value: "AQ" },
  { name: "Antigua and Barbuda", value: "AG" },
  { name: "Argentina", value: "AR" },
  { name: "Armenia", value: "AM" },
  { name: "Aruba", value: "AW" },
  { name: "Australia", value: "AU" },
  { name: "Austria", value: "AT" },
  { name: "Azerbaijan", value: "AZ" },
  { name: "Bahamas", value: "BS" },
  { name: "Bahrain", value: "BH" },
  { name: "Bangladesh", value: "BD" },
  { name: "Barbados", value: "BB" },
  { name: "Belarus", value: "BY" },
  { name: "Belgium", value: "BE" },
  { name: "Belize", value: "BZ" },
  { name: "Benin", value: "BJ" },
  { name: "Bermuda", value: "BM" },
  { name: "Bhutan", value: "BT" },
  { name: "Bolivia, Plurinational State of", value: "BO" },
  { name: "Bonaire, Sint Eustatius and Saba", value: "BQ" },
  { name: "Bosnia and Herzegovina", value: "BA" },
  { name: "Botswana", value: "BW" },
  { name: "Bouvet Island", value: "BV" },
  { name: "Brazil", value: "BR" },
  { name: "British Indian Ocean Territory", value: "IO" },
  { name: "Brunei Darussalam", value: "BN" },
  { name: "Bulgaria", value: "BG" },
  { name: "Burkina Faso", value: "BF" },
  { name: "Burundi", value: "BI" },
  { name: "Cambodia", value: "KH" },
  { name: "Cameroon", value: "CM" },
  { name: "Canada", value: "CA" },
  { name: "Cape Verde", value: "CV" },
  { name: "Cayman Islands", value: "KY" },
  { name: "Central African Republic", value: "CF" },
  { name: "Chad", value: "TD" },
  { name: "Chile", value: "CL" },
  { name: "China", value: "CN" },
  { name: "Christmas Island", value: "CX" },
  { name: "Cocos (Keeling) Islands", value: "CC" },
  { name: "Colombia", value: "CO" },
  { name: "Comoros", value: "KM" },
  { name: "Congo", value: "CG" },
  { name: "Congo, the Democratic Republic of the", value: "CD" },
  { name: "Cook Islands", value: "CK" },
  { name: "Costa Rica", value: "CR" },
  { name: "Côte d'Ivoire", value: "CI" },
  { name: "Croatia", value: "HR" },
  { name: "Cuba", value: "CU" },
  { name: "Curaçao", value: "CW" },
  { name: "Cyprus", value: "CY" },
  { name: "Czech Republic", value: "CZ" },
  { name: "Denmark", value: "DK" },
  { name: "Djibouti", value: "DJ" },
  { name: "Dominica", value: "DM" },
  { name: "Dominican Republic", value: "DO" },
  { name: "Ecuador", value: "EC" },
  { name: "Egypt", value: "EG" },
  { name: "El Salvador", value: "SV" },
  { name: "Equatorial Guinea", value: "GQ" },
  { name: "Eritrea", value: "ER" },
  { name: "Estonia", value: "EE" },
  { name: "Ethiopia", value: "ET" },
  { name: "Falkland Islands (Malvinas)", value: "FK" },
  { name: "Faroe Islands", value: "FO" },
  { name: "Fiji", value: "FJ" },
  { name: "Finland", value: "FI" },
  { name: "France", value: "FR" },
  { name: "French Guiana", value: "GF" },
  { name: "French Polynesia", value: "PF" },
  { name: "French Southern Territories", value: "TF" },
  { name: "Gabon", value: "GA" },
  { name: "Gambia", value: "GM" },
  { name: "Georgia", value: "GE" },
  { name: "Germany", value: "DE" },
  { name: "Ghana", value: "GH" },
  { name: "Gibraltar", value: "GI" },
  { name: "Greece", value: "GR" },
  { name: "Greenland", value: "GL" },
  { name: "Grenada", value: "GD" },
  { name: "Guadeloupe", value: "GP" },
  { name: "Guam", value: "GU" },
  { name: "Guatemala", value: "GT" },
  { name: "Guernsey", value: "GG" },
  { name: "Guinea", value: "GN" },
  { name: "Guinea-Bissau", value: "GW" },
  { name: "Guyana", value: "GY" },
  { name: "Haiti", value: "HT" },
  { name: "Heard Island and McDonald Islands", value: "HM" },
  { name: "Holy See (Vatican City State)", value: "VA" },
  { name: "Honduras", value: "HN" },
  { name: "Hong Kong", value: "HK" },
  { name: "Hungary", value: "HU" },
  { name: "Iceland", value: "IS" },
  { name: "India", value: "IN" },
  { name: "Indonesia", value: "ID" },
  { name: "Iran, Islamic Republic of", value: "IR" },
  { name: "Iraq", value: "IQ" },
  { name: "Ireland", value: "IE" },
  { name: "Isle of Man", value: "IM" },
  { name: "Israel", value: "IL" },
  { name: "Italy", value: "IT" },
  { name: "Jamaica", value: "JM" },
  { name: "Japan", value: "JP" },
  { name: "Jersey", value: "JE" },
  { name: "Jordan", value: "JO" },
  { name: "Kazakhstan", value: "KZ" },
  { name: "Kenya", value: "KE" },
  { name: "Kiribati", value: "KI" },
  { name: "Korea, Democratic People's Republic of", value: "KP" },
  { name: "Korea, Republic of", value: "KR" },
  { name: "Kuwait", value: "KW" },
  { name: "Kyrgyzstan", value: "KG" },
  { name: "Lao People's Democratic Republic", value: "LA" },
  { name: "Latvia", value: "LV" },
  { name: "Lebanon", value: "LB" },
  { name: "Lesotho", value: "LS" },
  { name: "Liberia", value: "LR" },
  { name: "Libya", value: "LY" },
  { name: "Liechtenstein", value: "LI" },
  { name: "Lithuania", value: "LT" },
  { name: "Luxembourg", value: "LU" },
  { name: "Macao", value: "MO" },
  { name: "Macedonia, the Former Yugoslav Republic of", value: "MK" },
  { name: "Madagascar", value: "MG" },
  { name: "Malawi", value: "MW" },
  { name: "Malaysia", value: "MY" },
  { name: "Maldives", value: "MV" },
  { name: "Mali", value: "ML" },
  { name: "Malta", value: "MT" },
  { name: "Marshall Islands", value: "MH" },
  { name: "Martinique", value: "MQ" },
  { name: "Mauritania", value: "MR" },
  { name: "Mauritius", value: "MU" },
  { name: "Mayotte", value: "YT" },
  { name: "Mexico", value: "MX" },
  { name: "Micronesia, Federated States of", value: "FM" },
  { name: "Moldova, Republic of", value: "MD" },
  { name: "Monaco", value: "MC" },
  { name: "Mongolia", value: "MN" },
  { name: "Montenegro", value: "ME" },
  { name: "Montserrat", value: "MS" },
  { name: "Morocco", value: "MA" },
  { name: "Mozambique", value: "MZ" },
  { name: "Myanmar", value: "MM" },
  { name: "Namibia", value: "NA" },
  { name: "Nauru", value: "NR" },
  { name: "Nepal", value: "NP" },
  { name: "Netherlands", value: "NL" },
  { name: "New Caledonia", value: "NC" },
  { name: "New Zealand", value: "NZ" },
  { name: "Nicaragua", value: "NI" },
  { name: "Niger", value: "NE" },
  { name: "Nigeria", value: "NG" },
  { name: "Niue", value: "NU" },
  { name: "Norfolk Island", value: "NF" },
  { name: "Northern Mariana Islands", value: "MP" },
  { name: "Norway", value: "NO" },
  { name: "Oman", value: "OM" },
  { name: "Pakistan", value: "PK" },
  { name: "Palau", value: "PW" },
  { name: "Palestine, State of", value: "PS" },
  { name: "Panama", value: "PA" },
  { name: "Papua New Guinea", value: "PG" },
  { name: "Paraguay", value: "PY" },
  { name: "Peru", value: "PE" },
  { name: "Philippines", value: "PH" },
  { name: "Pitcairn", value: "PN" },
  { name: "Poland", value: "PL" },
  { name: "Portugal", value: "PT" },
  { name: "Puerto Rico", value: "PR" },
  { name: "Qatar", value: "QA" },
  { name: "Réunion", value: "RE" },
  { name: "Romania", value: "RO" },
  { name: "Russian Federation", value: "RU" },
  { name: "Rwanda", value: "RW" },
  { name: "Saint Barthélemy", value: "BL" },
  { name: "Saint Helena, Ascension and Tristan da Cunha", value: "SH" },
  { name: "Saint Kitts and Nevis", value: "KN" },
  { name: "Saint Lucia", value: "LC" },
  { name: "Saint Martin (French part)", value: "MF" },
  { name: "Saint Pierre and Miquelon", value: "PM" },
  { name: "Saint Vincent and the Grenadines", value: "VC" },
  { name: "Samoa", value: "WS" },
  { name: "San Marino", value: "SM" },
  { name: "Sao Tome and Principe", value: "ST" },
  { name: "Saudi Arabia", value: "SA" },
  { name: "Senegal", value: "SN" },
  { name: "Serbia", value: "RS" },
  { name: "Seychelles", value: "SC" },
  { name: "Sierra Leone", value: "SL" },
  { name: "Singapore", value: "SG" },
  { name: "Sint Maarten (Dutch part)", value: "SX" },
  { name: "Slovakia", value: "SK" },
  { name: "Slovenia", value: "SI" },
  { name: "Solomon Islands", value: "SB" },
  { name: "Somalia", value: "SO" },
  { name: "South Africa", value: "ZA" },
  { name: "South Georgia and the South Sandwich Islands", value: "GS" },
  { name: "South Sudan", value: "SS" },
  { name: "Spain", value: "ES" },
  { name: "Sri Lanka", value: "LK" },
  { name: "Sudan", value: "SD" },
  { name: "Suriname", value: "SR" },
  { name: "Svalbard and Jan Mayen", value: "SJ" },
  { name: "Swaziland", value: "SZ" },
  { name: "Sweden", value: "SE" },
  { name: "Switzerland", value: "CH" },
  { name: "Syrian Arab Republic", value: "SY" },
  { name: "Taiwan, Province of China", value: "TW" },
  { name: "Tajikistan", value: "TJ" },
  { name: "Tanzania, United Republic of", value: "TZ" },
  { name: "Thailand", value: "TH" },
  { name: "Timor-Leste", value: "TL" },
  { name: "Togo", value: "TG" },
  { name: "Tokelau", value: "TK" },
  { name: "Tonga", value: "TO" },
  { name: "Trinidad and Tobago", value: "TT" },
  { name: "Tunisia", value: "TN" },
  { name: "Turkey", value: "TR" },
  { name: "Turkmenistan", value: "TM" },
  { name: "Turks and Caicos Islands", value: "TC" },
  { name: "Tuvalu", value: "TV" },
  { name: "Uganda", value: "UG" },
  { name: "Ukraine", value: "UA" },
  { name: "United Arab Emirates", value: "AE" },
  { name: "United Kingdom", value: "GB" },
  { name: "United States of America", value: "US" },
  { name: "United States Minor Outlying Islands", value: "UM" },
  { name: "Uruguay", value: "UY" },
  { name: "Uzbekistan", value: "UZ" },
  { name: "Vanuatu", value: "VU" },
  { name: "Venezuela, Bolivarian Republic of", value: "VE" },
  { name: "Viet Nam", value: "VN" },
  { name: "Virgin Islands, British", value: "VG" },
  { name: "Virgin Islands, U.S.", value: "VI" },
  { name: "Wallis and Futuna", value: "WF" },
  { name: "Western Sahara", value: "EH" },
  { name: "Yemen", value: "YE" },
  { name: "Zambia", value: "ZM" },
  { name: "Zimbabwe", value: "ZW" },
];
